import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang, LanguageSwitcher } from "@/lib/lang";
import { LayoutDashboard, Ticket, Percent, Code2, FileText, LogOut, Building2, Users, Car, Wallet, Globe, Gift, User as UserIcon, ShoppingBag, Receipt, FileSignature, Route, Map, Cog, Calendar as CalendarIcon } from "lucide-react";

export function PortalLayout({ portalKey, navItems, brandColor = "#0a0f1e" }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-white flex">
      <aside className="w-60 border-r border-slate-200 fixed h-screen flex flex-col bg-[#fafafa] z-30">
        <div className="p-5 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-[#f59e0b] flex items-center justify-center">
              <span className="font-display text-[#f59e0b] text-lg">D</span>
            </div>
            <div>
              <div className="font-display text-lg text-[#0a0f1e] leading-none">DiTours</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-1 font-semibold">{t(portalKey)}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`portal-nav-${item.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 ${
                  isActive ? "border-[#f59e0b] text-[#0a0f1e] bg-white font-semibold" : "border-transparent text-slate-600 hover:text-[#0a0f1e] hover:bg-white"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{t(item.key)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-5 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-3.5 h-3.5 text-slate-500" />
            <LanguageSwitcher testId="portal-lang-switch" />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("signed_in_as")}</div>
          <div className="text-xs text-[#0a0f1e] truncate">{user?.name}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          <button onClick={async () => { await logout(); nav("/login"); }} data-testid="portal-logout" className="mt-3 flex items-center gap-2 text-xs text-slate-500 hover:text-[#f59e0b]">
            <LogOut className="w-3.5 h-3.5" /> {t("sign_out")}
          </button>
        </div>
      </aside>
      <main className="ml-60 flex-1 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}

export default function PartnerLayout() {
  return (
    <PortalLayout
      portalKey="partner_portal"
      navItems={[
        { to: "/partner-portal", key: "nav_dashboard", icon: LayoutDashboard, end: true },
        { to: "/partner-portal/bookings", key: "nav_bookings", icon: Ticket },
        { to: "/partner-portal/tours", key: "nav_tours", icon: Map },
        { to: "/partner-portal/fixed-routes", key: "nav_fixed_routes", icon: Route },
        { to: "/partner-portal/vehicle-categories", key: "nav_vehicle_categories", icon: Cog },
        { to: "/partner-portal/seasons", key: "nav_seasons", icon: CalendarIcon },
        { to: "/partner-portal/markup", key: "nav_markup", icon: Percent },
        { to: "/partner-portal/embed", key: "nav_embed", icon: Code2 },
        { to: "/partner-portal/statements", key: "nav_statements", icon: FileText },
      ]}
    />
  );
}

export function FleetPortalLayout() {
  return (
    <PortalLayout
      portalKey="fleet_portal"
      navItems={[
        { to: "/fleet-portal", key: "nav_dashboard", icon: LayoutDashboard, end: true },
        { to: "/fleet-portal/drivers", key: "nav_drivers", icon: Users },
        { to: "/fleet-portal/vehicles", key: "nav_vehicles", icon: Car },
        { to: "/fleet-portal/earnings", key: "nav_earnings", icon: Wallet },
      ]}
    />
  );
}

export function ClientPortalLayout() {
  return (
    <PortalLayout
      portalKey="client_portal"
      navItems={[
        { to: "/client-portal", key: "nav_dashboard", icon: LayoutDashboard, end: true },
        { to: "/client-portal/bookings", key: "nav_bookings", icon: Ticket },
        { to: "/client-portal/referrals", key: "nav_referrals", icon: Gift },
        { to: "/client-portal/profile", key: "nav_profile", icon: UserIcon },
      ]}
    />
  );
}

export function OwnerPortalLayout() {
  return (
    <PortalLayout
      portalKey="owner_portal"
      navItems={[
        { to: "/owner-portal", key: "nav_dashboard", icon: LayoutDashboard, end: true },
        { to: "/owner-portal/vehicles", key: "nav_vehicles", icon: Car },
        { to: "/owner-portal/contracts", key: "nav_contracts", icon: FileSignature },
        { to: "/owner-portal/payments", key: "nav_payments", icon: Wallet },
        { to: "/owner-portal/expenses", key: "nav_expenses", icon: Receipt },
      ]}
    />
  );
}

export function PortalPageHeader({ eyebrow, title, description, children }) {
  return (
    <div className="flex items-end justify-between mb-8 pb-5 border-b border-slate-200">
      <div>
        {eyebrow && <p className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-2">{eyebrow}</p>}
        <h1 className="font-display text-3xl text-[#0a0f1e] tracking-tight">{title}</h1>
        {description && <p className="text-slate-500 mt-2 text-sm max-w-2xl">{description}</p>}
      </div>
      {children && <div className="flex items-center gap-3">{children}</div>}
    </div>
  );
}
